/**
 * Diwali gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_diwali`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diwaliGiftTagsPack: ToilePack = {
    slug: 'diwali-gift-tags',
    category: 'faith',
    name: 'Diwali gift tags',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'greeting', label: 'Happy Diwali', subject: 'a sheet of six rectangular tags reading "Happy Diwali", each with a small diya, a punch hole at the top and a blank line for the name'},
        {key: 'shubh', label: 'Shubh Deepavali', subject: 'a sheet of six round tags reading "Shubh Deepavali" with a marigold ring around the edge and a blank "To" line'},
        {key: 'fromto', label: 'To and from', subject: 'a sheet of eight scalloped tags with "To" and "From" lines, a tiny lantern on each tag and no other text'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft paper', style: 'tags in white and dark ink as if printed on kraft paper, hand-stamped look, simple line motifs', palette: 'kraft brown with white and dark brown ink', framing: 'Flat on the sheet, tags evenly spaced'},
        {key: 'flatcolor', label: 'Flat color', style: 'flat vector tags, bold clean shapes, hard edges, no outlines', palette: 'saffron, magenta, deep green and cream, four colors', framing: 'Tags on a plain white ground, evenly spaced'},
        {key: 'linetag', label: 'Line art', style: 'fine black line art, uniform thin stroke, closed shapes, no fills', palette: 'black ink only', framing: 'Tags on plain white paper with dashed cut lines'},
    ],
    directions: ['Ribbon', 'Gold ink', 'Round tags'],
    directionHint: 'try: Ribbon',
    seo: {
        title: 'Diwali gift tags: Happy Diwali, Shubh Deepavali',
        metaDescription: 'Printable Diwali gift tags for sweets and boxes, lettered Happy Diwali or Shubh Deepavali, with a punched hole and a name line, in three styles.',
        h1: 'Diwali gift tags: Happy Diwali, Shubh Deepavali, to and from',
        tagline: 'Print-ready sheets of cut tags, each with its greeting lettered exactly and a line for the name.',
        intro: 'Tags to print, cut and tie to dry-fruit boxes, sweets and gifts for family, neighbours and colleagues at Diwali. Each sheet holds an even grid of tags: rectangular tags reading Happy Diwali, round tags reading Shubh Deepavali with a marigold ring, and scalloped to and from tags with a tiny lantern. Each has a punched hole and a blank line for the name. The styles are kraft paper, flat colour, and fine black line art.',
        useCases: [
            'dry-fruit boxes and sweets',
            'gifts for neighbours and colleagues',
            'family gifts with a name line',
            'a tag tied to a box with ribbon',
        ],
        faq: [
            {q: 'Which greeting is on each tag?', a: 'Happy Diwali on the rectangular tags, Shubh Deepavali on the round tags, and a To and From line on the scalloped tags.'},
            {q: 'Where is the hole?', a: 'Each tag has a punched hole for a ribbon or string.'},
            {q: 'How do I cut them out?', a: 'Cut each tag out of the sheet along the spaces between them, then tie it to its gift.'},
            {q: 'Can I ask for a different look?', a: 'Yes. The direction field takes a change such as Ribbon, Gold ink or Round tags. A direction you type is a request, not a promised result.'},
        ],
    },
}
